import { createContext, useCallback, useContext, useEffect, useState } from 'react';

import { BANNER_STORAGE_KEY, SHOW_BANNER_DEFAULT } from '@/constants/banner';

import { AnnouncementBannerProps } from '@/ui/AnnouncementBanner';

export type CustomAnnouncementContent = Pick<
  AnnouncementBannerProps,
  'variant' | 'title' | 'onDismiss'
> & {
  description: string;
};

const AnnouncementContext = createContext<{
  showBanner: boolean;
  hideBanner: () => void;
  customContent?: CustomAnnouncementContent;
  setCustomContent: (content?: CustomAnnouncementContent) => void;
}>({
  showBanner: false,
  hideBanner: () => {},
  setCustomContent: () => {},
});

export function AnnouncementBannerProvider({ children }: { children: React.ReactNode }) {
  const [showBanner, setShowBanner] = useState(SHOW_BANNER_DEFAULT);
  const [customContent, setCustomContent] = useState<CustomAnnouncementContent>();
  const shouldShowBanner = showBanner || customContent != null;

  const hideBanner = useCallback(() => {
    window.localStorage.setItem(BANNER_STORAGE_KEY, 'true');
    document.body.dataset.showBanner = 'false';
    setShowBanner(false);
  }, []);

  // Read the pre-hydration decision from _document without changing server/client markup during hydration.
  useEffect(() => {
    setShowBanner(document.body.dataset.showBanner === 'true');
  }, []);

  useEffect(() => {
    document.body.dataset.showBanner = shouldShowBanner.toString();
  }, [shouldShowBanner]);

  return (
    <AnnouncementContext.Provider
      value={{
        showBanner: shouldShowBanner,
        hideBanner,
        customContent,
        setCustomContent,
      }}
    >
      {children}
    </AnnouncementContext.Provider>
  );
}

export function useAnnouncementBanner() {
  return useContext(AnnouncementContext);
}
